/**
 * Tabs class recipe — composes size + color ingredients.
 * Shape: Tabs.size.ts. Ink: Tabs.color.ts.
 */

import { cn } from '@/utils/cn';
import {
  TABS_COUNT_ACTIVE,
  TABS_COUNT_INACTIVE,
  TABS_DESCRIPTION,
  TABS_DESCRIPTION_ON_DARK,
  TABS_FOCUS,
  TABS_ICON_FILL_ACTIVE,
  TABS_ICON_INACTIVE,
  TABS_ICON_LINE_ACTIVE,
  TABS_LABEL_FILL_ACTIVE,
  TABS_LABEL_INACTIVE_VERTICAL,
  TABS_LABEL_LINE_ACTIVE,
  TABS_LINE_ACTIVE,
  TABS_LINE_INACTIVE,
  TABS_PILL_ACTIVE,
  TABS_PILL_INACTIVE,
  TABS_TRACK,
  TABS_VERTICAL_FILL_ACTIVE,
  TABS_VERTICAL_FILL_INACTIVE,
  TABS_VERTICAL_LINE_ACTIVE,
  TABS_VERTICAL_LINE_INACTIVE,
} from './Tabs.color';
import {
  TABS_COUNT,
  TABS_ICON_GLYPH,
  TABS_PILL_TRACK,
  TABS_TYPE,
  getTabsTriggerSizeClass,
} from './Tabs.size';
import type { TabsColor, TabsOrientation, TabsSize, TabsVariant } from './Tabs.types';

const FOCUS_BASE = 'focus-visible:outline-none focus-visible:ring-2';

export function getTabsListClassName(
  orientation: TabsOrientation,
  variant: TabsVariant,
  size: TabsSize,
  color: TabsColor,
  className?: string,
): string {
  if (orientation === 'vertical') {
    return cn('flex flex-col gap-0.5', className);
  }

  if (variant === 'line') {
    return cn('flex items-center gap-1', className);
  }

  return cn(
    'inline-flex items-center rounded-full',
    TABS_PILL_TRACK[size],
    TABS_TRACK[color],
    className,
  );
}

export function getTabsTriggerClassName(
  orientation: TabsOrientation,
  variant: TabsVariant,
  size: TabsSize,
  color: TabsColor,
  isActive: boolean,
  disabled: boolean,
): string {
  const sizeBox = getTabsTriggerSizeClass(orientation, variant, size);
  const type = TABS_TYPE[size];
  const focus = cn(FOCUS_BASE, TABS_FOCUS[color]);

  if (orientation === 'vertical') {
    if (variant === 'line') {
      return cn(
        'w-full flex items-start text-left transition-colors rounded-none',
        'border-l-[3px]',
        sizeBox,
        type,
        focus,
        disabled && 'opacity-50 cursor-not-allowed',
        isActive ? TABS_VERTICAL_LINE_ACTIVE[color] : TABS_VERTICAL_LINE_INACTIVE[color],
      );
    }

    return cn(
      'w-full flex items-center rounded-lg text-left transition-colors',
      sizeBox,
      type,
      focus,
      disabled && 'opacity-50 cursor-not-allowed',
      isActive ? TABS_VERTICAL_FILL_ACTIVE[color] : TABS_VERTICAL_FILL_INACTIVE[color],
    );
  }

  if (variant === 'line') {
    return cn(
      'shrink-0 inline-flex items-center transition-colors border-b-2',
      'focus-visible:ring-offset-1',
      sizeBox,
      type,
      focus,
      disabled && 'opacity-50 cursor-not-allowed',
      isActive ? TABS_LINE_ACTIVE[color] : TABS_LINE_INACTIVE[color],
    );
  }

  return cn(
    'shrink-0 inline-flex items-center justify-center rounded-full transition-colors',
    sizeBox,
    type,
    focus,
    disabled && 'opacity-50 cursor-not-allowed',
    isActive ? TABS_PILL_ACTIVE[color] : TABS_PILL_INACTIVE[color],
  );
}

export function getTabsIconClassName(
  orientation: TabsOrientation,
  variant: TabsVariant,
  size: TabsSize,
  color: TabsColor,
  isActive: boolean,
): string {
  const glyph = TABS_ICON_GLYPH[size];
  // Force SVG to inherit wrapper ink so call-site icon colors cannot diverge from the label.
  const inherit = '[&_svg]:text-current';
  if (!isActive) {
    return cn('shrink-0', glyph, inherit, TABS_ICON_INACTIVE);
  }
  if (orientation === 'vertical' && variant === 'line') {
    return cn('shrink-0', glyph, inherit, TABS_ICON_LINE_ACTIVE[color]);
  }
  return cn('shrink-0', glyph, inherit, TABS_ICON_FILL_ACTIVE[color]);
}

export function getTabsLabelClassName(
  orientation: TabsOrientation,
  variant: TabsVariant,
  color: TabsColor,
  isActive: boolean,
): string {
  if (orientation !== 'vertical') {
    return 'truncate';
  }
  if (!isActive) {
    return cn('block truncate', TABS_LABEL_INACTIVE_VERTICAL);
  }
  if (variant === 'line') {
    return cn('block truncate', TABS_LABEL_LINE_ACTIVE[color]);
  }
  return cn('block truncate', TABS_LABEL_FILL_ACTIVE[color]);
}

export function getTabsDescriptionClassName(
  color: TabsColor,
  variant: TabsVariant,
  isActive: boolean,
): string {
  if (isActive && variant === 'default' && color === 'secondary') {
    return TABS_DESCRIPTION_ON_DARK;
  }
  return TABS_DESCRIPTION;
}

export function getTabsCountClassName(
  size: TabsSize,
  color: TabsColor,
  isActive: boolean,
): string {
  return cn(
    'inline-flex items-center justify-center rounded-full font-semibold tabular-nums font-body',
    TABS_COUNT[size],
    isActive ? TABS_COUNT_ACTIVE[color] : TABS_COUNT_INACTIVE[color],
  );
}
